import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Col,
  Divider,
  Drawer,
  Form,
  Modal,
  Progress,
  Row,
  Select,
  DatePicker as AntdDatePicker,
  Switch
} from "antd";
import { connect } from "react-redux";
import randomUid from "uid";
import { compare } from 'natural-orderby';

import * as checklistActions from "~/containers/Checklist/actions";
import * as drawerChecklistActions from "~/containers/DrawerChecklist/actions";

import { DatePicker } from "~/components/UI/DatePicker/Locale";
import moment from "moment";
import TasksModal from "./Modals/TasksModal";
import TasksTable from "./Tables/TasksTable";
import { PERMISSIONS } from '~/constants/permissions.constants';
import Tip from '~/components/Tip';

const { Option } = Select;
const { OptGroup } = Select;
const { MonthPicker } = AntdDatePicker;

const DEFAULT_MODAL_DATA = {
  uid: undefined,
  department: undefined,
  task: undefined,
  done: undefined,
  index: undefined,
};

function DrawerChecklist(props) {
  const { drawer, customers, user, permissions, checklistTasks, form } = props;
  const { getFieldDecorator, getFieldValue, setFields } = form;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [deletedTasks, setDeletedTasks] = useState([]);
  const [showTasksRequiredError, setShowTasksRequiredError] = useState(false);
  const [defaultModalData, setDefaultModalData] = useState(DEFAULT_MODAL_DATA);
  const [prevStatus, setPrevStatus] = useState(null);
  const [defaultFilteredDepartments, setDefaultFilteredDepartments] = useState([]);
  const [isRevised, setIsRevised] = useState(false);

  const hasReviewPermission = () => {
    let userPermissions = [];
    
    if (!user.permissions_group_id) {
      userPermissions = user.permissions;
    } else {
      const permissionsGroup = permissions.find(
        (item) => item.id === user.permissions_group_id
      );
      if (!permissionsGroup) return false;
      userPermissions = permissionsGroup.permissions;
    }

    return userPermissions.includes(PERMISSIONS.checklist.review);
  };

  useEffect(() => {
    if (drawer.show && drawer.data.id) {
      setPrevStatus(drawer.data.status);
      setTasks(drawer.data.tasks);
      setDefaultFilteredDepartments(getDefaultFilteredValue());
      setIsRevised(drawer.data.is_revised);
    }
    // eslint-disable-next-line
  }, [drawer.show]);

  const closeDrawer = useCallback(() => {
    setTasks([]);
    props.closeDrawer();
  }, [props]);

  const closeModal = () => {
    setIsModalOpen(false);
    setDefaultModalData(DEFAULT_MODAL_DATA);
  };

  const openModal = () => setIsModalOpen(true);

  const showTasksRequiredErrorFn = () => setShowTasksRequiredError(true);
  const hideTasksRequiredError = () => setShowTasksRequiredError(false);

  const handleSaveTask = () => {
    form.validateFields(["task", "department"], (error, values) => {
      if (error) return;
      const { task, department } = values;
      const { uid, done, index } = defaultModalData;
      if (uid) {
        setTasks(tasks.map((item) =>
          item.uid !== uid
            ? item
            : { uid, name: task, department, done, index }
        ));
      } else {
        setTasks([
          ...tasks,
          {
            uid: randomUid(10),
            name: task,
            department,
            done: 0,
            index: tasks.length + 1,
          },
        ]);
      }
      closeModal();
      hideTasksRequiredError();
    });
  };

  const handleRemoveTask = (uid) => {
    Modal.confirm({
      title: "Você realmente deseja excluir esta tarefa?",
      content: `Esta ação não poderá ser desfeita.`,
      okText: "Sim",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => {
        const deletedTask = tasks.find((task) => task.uid === uid);
        setTasks(tasks.filter((task) => task.uid !== uid));
        setDeletedTasks([...deletedTasks, deletedTask]);
      },
    });
  };

  const handleEditTask = (uid) => {
    const currentTask = tasks.find((task) => task.uid === uid);
    setDefaultModalData({
      uid: currentTask.uid,
      department: currentTask.department,
      task: currentTask.name,
      done: currentTask.done,
      index: currentTask.index,
    });
    openModal();
  };

  const handleCheckTask = (uid) => {
    setTasks(tasks.map((task) =>
      task.uid === uid ? { ...task, done: task.done === 1 ? 0 : 1 } : task
    ));
  };

  const handleConfirmChangeStatus = (status) => {
    setFields({
      status: {
        value: prevStatus
      }
    });
    Modal.confirm({
      title: 'Você realmente deseja alterar o status?',
      content: 'As alterações nas tarefas que foram feitas antes de salvar serão perdidas.',
      okText: 'Sim',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => {
        if (prevStatus !== status) {
          setPrevStatus(status);
        }
        handleChangeStatus(status);
      }
    });
  };

  const handleChangeStatus = (status) => {
    setFields({
      status: {
        value: status
      }
    });
    if (status === 1) {
      const closingOutputTasks = checklistTasks.filter(({ status }) => status === 1);
      const formattedClosingOutputTasks = closingOutputTasks.map((task, index) => ({
        uid: randomUid(10),
        ...task,
        done: 0,
        index,
      }));
      setTasks(formattedClosingOutputTasks);
      return hideTasksRequiredError();
    }
    if (status === 2) {
      const inputTasks = checklistTasks.filter(({ status }) => status === 2);
      const formattedInputTasks = inputTasks.map((task, index) => ({
        uid: randomUid(10),
        ...task,
        done: 0,
        index,
      }));
      setTasks(formattedInputTasks);
      return hideTasksRequiredError();
    }
    if (status === 4) {
      const inputTasks = checklistTasks.filter(({ status }) => status === 4);
      const formattedInputTasks = inputTasks.map((task, index) => ({
        uid: randomUid(10),
        ...task,
        done: 0,
        index,
      }));
      setTasks(formattedInputTasks);
      return hideTasksRequiredError();
    }
    if (status === 3) {
      const changingOutputTasks = checklistTasks.filter(({ status }) => status === 3);
      const formattedChangingOutputTasks = changingOutputTasks.map((task, index) => ({
        uid: randomUid(10),
        ...task,
        done: 0,
        index
      }));
      setTasks(formattedChangingOutputTasks);
      return hideTasksRequiredError();
    }
    setTasks([]);
    setDeletedTasks(tasks);
  };

  const handleSubmit = () => {
    form.validateFields([
      "customer_id",
      "status",
      "requested_at",
      "competency_at",
      "due_at"
    ], (error, values) => {
      if (tasks.length === 0) return showTasksRequiredErrorFn();
      if (error) return;
      hideTasksRequiredError();
      const isUpdating = !!drawer.data.id;
      try {
        const data = {
          ...values,
          requested_at: moment(values.requested_at).format("YYYY-MM-DD"),
          competency_at: moment(values.competency_at).set({ date: 1 }).format("YYYY-MM-DD"),
          due_at: moment(values.due_at).set({ date: 1 }).format("YYYY-MM-DD"),
          tasks,
          is_revised: isRevised
        };
        if (isUpdating) {
          data.deleted_tasks = deletedTasks;
          const checklistId = drawer.data.id;
          props.updateChecklist(data, checklistId);
        } else {
          props.createChecklist(data);
        }
      } catch (error) {
        console.log(error);
      } finally {
        closeDrawer();
      }
    });
  };

  const handleState = (state) => {
    if (state.tasks !== undefined) setTasks(state.tasks);
    if (state.deletedTasks !== undefined) setDeletedTasks(state.deletedTasks);
    if (state.showTasksRequiredError !== undefined) setShowTasksRequiredError(state.showTasksRequiredError);
    if (state.defaultModalData !== undefined) setDefaultModalData(state.defaultModalData);
    if (state.prevStatus !== undefined) setPrevStatus(state.prevStatus);
    if (state.defaultFilteredDepartments !== undefined) setDefaultFilteredDepartments(state.defaultFilteredDepartments);
    if (state.is_revised !== undefined) setIsRevised(state.is_revised);
  };

  function getDefaultFilteredValue() {
    const departmentsOfPermissions = {
      [PERMISSIONS.checklist.columns.legalization]: 1,
      [PERMISSIONS.checklist.columns.administrative]: 2,
      [PERMISSIONS.checklist.columns.accounting]: 3,
      [PERMISSIONS.checklist.columns.fiscal]: 4,
      [PERMISSIONS.checklist.columns.personal]: 5,
      [PERMISSIONS.checklist.columns.cs]: 6,
    };
    const permissionGroup = permissions.find((item) => item.id === user.permissions_group_id);
    const departments = [];
    if (!permissionGroup) {
      if (user.permissions) {
        user.permissions.forEach((item) => {
          if (typeof departmentsOfPermissions[item] !== 'undefined') {
            departments.push(departmentsOfPermissions[item]);
          }
        });
        return departments;
      } else {
        return [];
      }
    }
    permissionGroup.permissions.forEach((item) => {
      if (departmentsOfPermissions[item]) {
        departments.push(departmentsOfPermissions[item]);
      }
    });
    return departments;
  }

  function getConcludedTasksPercentage() {
    if (defaultFilteredDepartments.length > 0) {
      const tasksBasedInPermissions = tasks.filter((task) => defaultFilteredDepartments.includes(task.department));
      const concludedTasksCount = tasksBasedInPermissions.filter((task) => task.done).length;
      return Math.ceil((100 * concludedTasksCount) / tasksBasedInPermissions.length);
    } else {
      const concludedTasksCount = tasks.filter((task) => task.done).length;
      return Math.ceil((100 * concludedTasksCount) / tasks.length);
    }
  }

  if (!customers) return null;
  const data = drawer.data;
  const activeCustomers = customers.filter((customer) => customer.enabled);
  const pendingCustomers = customers.filter((customer) => !customer.enabled);

  return (
    <Drawer
      maskClosable={false}
      destroyOnClose
      visible={drawer.show}
      title={data.id ? "Editar item" : "Cadastrar novo item"}
      width={800}
      onClose={closeDrawer}
    >
      <Form layout="vertical">
        <Form.Item label="Cliente">
          {getFieldDecorator("customer_id", {
            initialValue: data.customer_id ? String(data.customer_id) : undefined,
            rules: [
              {
                required: true,
                message: "Por favor, informe o nome do cliente",
              },
            ],
          })(
            <Select
              placeholder="Selecione um cliente"
              showSearch
              optionFilterProp="children"
              filterOption={true}
            >
              <OptGroup label="Ativos">
                {activeCustomers.map((customer) => (
                  <Option
                    key={customer.id}
                    value={String(customer.id)}
                  >{`${customer.cod} - ${customer.name}`}</Option>
                ))}
              </OptGroup>
              <OptGroup label={<span>Pendentes <Tip>São clientes que ainda estão em processo no Dep. Societário.</Tip></span>}>
                {pendingCustomers.map((customer) => (
                  <Option
                    key={customer.id}
                    value={String(customer.id)}
                  >{`${customer.cod} - ${customer.name}`} <Tip>Este cliente ainda está em processo no Dep. Societário.</Tip></Option>
                ))}
              </OptGroup>
            </Select>
          )}
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Status">
              {getFieldDecorator("status", {
                initialValue: data.status,
                rules: [
                  {
                    required: true,
                    message: "Por favor, informe o status",
                  },
                ],
              })(
                <Select
                  placeholder="Selecione um status"
                  onSelect={data.id
                    ? handleConfirmChangeStatus
                    : handleChangeStatus
                  }
                >
                  <Option value={2}>Entrada</Option>
                  <Option value={4}>Entrada (Troca de Contabilidade)</Option>
                  <Option value={1}>Saída (Encerramento)</Option>
                  <Option value={3}>Saída (Troca de Contabilidade)</Option>
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Data da solicitação">
              {getFieldDecorator("requested_at", {
                initialValue: data.requested_at
                  ? moment(data.requested_at)
                  : undefined,
                rules: [
                  {
                    required: true,
                    message: "Por favor, informe a data de solicitação",
                  },
                ],
              })(<DatePicker />)}
            </Form.Item>
          </Col>
        </Row>
        <Divider>Responsável {[1, 3].includes(getFieldValue('status')) ? 'até' : 'a partir de'}</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Competência">
              {getFieldDecorator("competency_at", {
                initialValue: data.competency_at
                  ? moment(data.competency_at)
                  : undefined,
              })(
                <MonthPicker
                  placeholder="Selecione uma data"
                  format={'MM/YYYY'}
                  style={{ width: '100%' }}
                />
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Vencimento">
              {getFieldDecorator("due_at", {
                initialValue: data.due_at
                  ? moment(data.due_at)
                  : undefined,
              })(
                <MonthPicker
                  placeholder="Selecione uma data"
                  format={'MM/YYYY'}
                  style={{ width: '100%' }}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
        <Divider>Tarefas</Divider>
        {tasks.length > 0 && (
          <>
            <Progress percent={getConcludedTasksPercentage()} />
            <TasksTable
              tasks={tasks}
              onCheckTask={handleCheckTask}
              onEditTask={handleEditTask}
              onRemoveTask={handleRemoveTask}
              onSetState={handleState}
              state={{
                isModalOpen,
                tasks,
                deletedTasks,
                showTasksRequiredError,
                defaultModalData,
                prevStatus,
                defaultFilteredDepartments,
                is_revised: isRevised,
              }}
              defaultFilteredDepartments={defaultFilteredDepartments}
            />
          </>
        )}
        <Button
          {...(showTasksRequiredError && {
            style: {
              borderColor: "#f5222d",
              color: "#f5222d",
            },
          })}
          onClick={openModal}
        >
          Adicionar tarefa
        </Button>
        {showTasksRequiredError && (
          <div
            style={{
              color: "#f5222d",
              marginTop: 2,
            }}
          >
            Por favor, adicione pelo menos uma tarefa
          </div>
        )}
        { data.id && hasReviewPermission() && (
          <Form.Item label="Revisado" style={{ marginTop: "20px" }}>
            <Switch 
              defaultChecked={!!isRevised}
              onChange={(checked) => setIsRevised(checked)}
            />
          </Form.Item>
        )}
      </Form>
      <TasksModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSaveTask={handleSaveTask}
        data={defaultModalData}
        getFieldDecorator={getFieldDecorator}
      />
      <div
        style={{
          borderTop: "1px solid #e8e8e8",
          textAlign: "right",
          background: "#fff",
          paddingTop: 10,
          marginTop: 25,
        }}
      >
        <Button onClick={closeDrawer}>Cancelar</Button>
        <Button
          type="primary"
          style={{ marginLeft: 8 }}
          onClick={handleSubmit}
        >
          {data.id ? "Salvar" : "Cadastrar"}
        </Button>
      </div>
      <Modal />
    </Drawer>
  );
}

const mapStateToProps = (state) => ({
  drawer: state.drawerChecklist,
  customers: state.customers.list.filter(row => row.is_customer).sort((a,b) => compare()(a.cod, b.cod)),
  user: state.user,
  permissions: state.permissions.list,
  checklistTasks: state.checklists.tasks
});

const mapDispatchProps = (dispatch) => ({
  closeDrawer: () => dispatch(drawerChecklistActions.closeDrawer()),
  createChecklist: (data) => dispatch(checklistActions.createChecklist(data)),
  updateChecklist: (data, id) =>
    dispatch(checklistActions.updateChecklist(data, id)),
});

export default connect(
  mapStateToProps,
  mapDispatchProps
)(Form.create()(DrawerChecklist));
